import {
  type PayloadAction,
  type UnknownAction,
  createSlice,
  isFulfilled,
  isPending,
  isRejected
} from "@reduxjs/toolkit";

import type { EventsArgs } from "../data/api/events.api";
import type { ClassicSnapshot } from "../domain/classic-carry-over";
import type { QuickFilterCode } from "../domain/counters";
import type { CreateDraft } from "../domain/create-draft";
import type { Occurrence } from "../domain/deletable";
import type { FollowUpOffer } from "../domain/follow-up-offer";
import type { EventKind } from "../domain/form-values";
import type { NotifyKeys } from "../domain/notify-channel";
import { isPersistedSettingKey } from "../domain/persisted-settings";
import type { ScheduleMoveRow } from "../domain/schedule-move";
import {
  type ReschedulePick,
  type RescheduleRequest,
  type SchedulingFor,
  suggestedType
} from "../domain/scheduling-for";
import {
  DEFAULT_SETTINGS,
  type PreviewMode,
  type SchedulerSettings,
  type SchedulerView
} from "../domain/settings";
import type {
  PersistedSettingKey,
  ScopeMode
} from "../domain/settings-document";
import type { SweepRecord } from "../domain/sweep";
import type {
  AppointmentStatusCode,
  AppointmentTypeId,
  ClinicId,
  ProviderId
} from "../domain/types";
import { stepDate } from "../engine/range";
import type { PendingRoomMove } from "../engine/room-traffic-pending";
import {
  type StaffContext,
  type StaffList,
  staffForPreset
} from "../engine/staff-presets";
import { isoDate } from "../engine/time";
import { eventsArgsFor } from "./scheduler-v3.selectors";

/** A status change on screen but not saved yet: it is sent when the Undo window closes. */
export interface PendingUndo {
  appointmentId: number;
  previousStatus: AppointmentStatusCode;
  status: AppointmentStatusCode;
  patientId?: number;
  /** Patient last name (or title), for the snackbar and error copy. */
  who: string;
  expiresAt: number;
  /** A cancel / no-show reason collected before the Undo window opened. */
  outcomeNotes?: string;
  /** Staff's answer to "Send cancellation message?", when the practice asks it. */
  sendCancellationMessage?: boolean;
  /**
   * Room Traffic's drop on Completed (ENG-2214): the patient's open room row, closed
   * immediately BEFORE the completion is written and never earlier — so a completion the
   * user abandons leaves them in their room with the row open. It cannot be closed after:
   * the backend hides a completed appointment's rows from every action.
   */
  leaveRoomRowId?: number;
  /**
   * The room that row is in, to put the patient back in if the completion is refused
   * after the checkout went through (ENG-2214).
   */
  leaveRoomId?: number;
}

/**
 * A drag-to-move waiting behind its Undo snackbar.
 *
 * It is held rather than sent for one reason: changing `start_moment` on a live
 * appointment makes the backend text the patient a reschedule message
 * (`models/appointment.py:2111` `reschedule_required`). Sending on drop would mean an
 * accidental drag messages a patient before the user has finished reacting to it. The
 * PATCH goes out when the window closes, exactly like a status change.
 */
export interface PendingMove {
  /**
   * The appointment, or for a schedule row's move (`schedule` set) its negated schedule id
   * (`scheduleMoveKey`), so the two id spaces never meet in anything keyed on this.
   */
  appointmentId: number;
  /**
   * Set when the card is a one-off personal or clinic event (ENG-2280): the same hold,
   * dialog and Undo, written to `/schedules/{id}/` instead. Nobody is texted for one.
   */
  schedule?: ScheduleMoveRow;
  /** Patient last name (or title), for the snackbar copy. */
  who: string;
  date: string;
  startMinutes: number;
  endMinutes: number;
  providerId: number | null;
  clinicId: number | null;
  /** Where it came from, so Undo and a refusal can both put it back. */
  previous: {
    start: string;
    end: string;
    providerId: number | null;
    clinicId: number;
  };
  expiresAt: number;
  /** The user was asked about a collision and said yes. */
  allowOverlap?: boolean;
  createScheduleForStaff?: boolean;
  /** Only the end changed: the snackbar and a refusal say "ends at", not "moved" (ENG-2053). */
  isResize?: boolean;
  /**
   * The channel the patient is told by, sent with the PATCH at commit. Undo sends nothing,
   * so nobody is told. Set only when the move messages the patient at all (a new start or
   * clinic, `engine/move.ts` `messagesPatient`); the keys are stored on the appointment.
   */
  notify?: NotifyKeys;
}

/**
 * A move sent to the server. `useV3Events` keeps drawing it until the server's rows are
 * known to have it: while its PATCH is in flight, and after the PATCH succeeds until a
 * read of the grid's range that STARTED after the success lands.
 *
 * The success alone is not enough. RTK Query delays an invalidation while any other
 * request is out, so a read that started before the server applied the move (the
 * previous move's refetch, or a WebSocket one) can land after the save with the card at
 * its old slot, and the save's own refetch only starts once it has.
 */
export interface SentMove {
  move: PendingMove;
  /** The PATCH succeeded. A refused move is removed instead: its thunk paints it back. */
  saved: boolean;
  /** The grid read that started after the save. The move stops being drawn when it lands. */
  readId?: string;
}

/**
 * The same move. Moves carry no id; two of one card differ in when they were made or where
 * they went, and two that match on all of these are the same change.
 */
export const isSameMove = (a: PendingMove, b: PendingMove): boolean =>
  a.appointmentId === b.appointmentId &&
  a.expiresAt === b.expiresAt &&
  a.date === b.date &&
  a.startMinutes === b.startMinutes &&
  a.endMinutes === b.endMinutes &&
  a.providerId === b.providerId &&
  a.clinicId === b.clinicId;

/**
 * A status change that has to ask something before it can be saved.
 *
 * It lives here rather than in the preview because the popover closes itself
 * synchronously after a status action (`event-popover.component.tsx:76-79`), so a panel
 * rendered inside it would unmount the instant the button was pressed.
 */
export interface CompletionRequest {
  appointmentId: number;
  patientId?: number;
  /** Completed, Canceled or NoShow — decides the heading and the sections. */
  status: AppointmentStatusCode;
  previousStatus: AppointmentStatusCode;
  /** Patient last name, for the panel's heading. */
  who: string;
  /** The room row to close just before the write — see `PendingUndo.leaveRoomRowId`. */
  leaveRoomRowId?: number;
  /** Its room — see `PendingUndo.leaveRoomId`. */
  leaveRoomId?: number;
}

/**
 * UI state of the V3 scheduler only. Server data (catalog, events) lives in the RTK
 * Query cache — see `data/api/` — so nothing here is a copy of the backend.
 */
/** Re-exported so importers that reach for it here keep working; it lives in settings. */
export type { PreviewMode };

/** A `DOMRect` reduced to what the store may hold: plain, serializable numbers. */
export interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Where the per-user cloud settings store has got to this session (ENG-2047).
 *
 * `idle` and `reading` hold the grid; everything else releases it. `cacheHydrated` is the
 * common returning-user case — the sessionStorage mirror painted the stored layout on the
 * first frame — and the authoritative server read still lands on top of it.
 */
export type SettingsSyncStatus =
  | "idle"
  | "reading"
  | "cacheHydrated"
  | "hydrated"
  | "readFailed"
  | "timedOut";

/**
 * Why the store is not saving, or not saving cleanly. Three of the four block writes
 * outright; `writeFailed` does not, because the write is being retried.
 */
export type SettingsHoldReason =
  /** A GET 404: this backend has no store. Silent by design — nothing has gone wrong. */
  | "unavailable"
  /** The read failed and we have no base to overlay onto: writing would clobber. */
  | "readFailed"
  /** A PUT is failing and being retried; the value is still on screen. */
  | "writeFailed"
  /** The stored document is newer than this build. Read what we know, write nothing. */
  | "versionHold";

/** The late-document notice. `applying` is the request the sync hook answers. */
export type SettingsNoticeState = "none" | "offered" | "applying" | "dismissed";

export interface SettingsSyncState {
  status: SettingsSyncStatus;
  /** `v3ScopeKey` the read was for. Read once per scope, never again while mounted. */
  hydratedFor: string | null;
  /** The practice every write of this session is addressed to. Fixed at hydration. */
  boundPracticeId: number | null;
  /**
   * The RAW namespace document behind the notice, not a reconciled one: Apply runs the
   * same reconciliation the first hydration does, against the catalog as it is by then.
   */
  lateDocument: unknown;
  settingsNotice: SettingsNoticeState;
  holdReason: SettingsHoldReason | null;
  /**
   * Persisted keys changed this session and not yet acknowledged by a 2xx.
   *
   * It lives here rather than in the hook because the REDUCERS are the only place that
   * knows a change came from the user: a diff of `settings` cannot tell a filter the user
   * ticked from the clinic defaults or the stored document being applied, and persisting
   * those would write an untouched default into everyone's row.
   */
  touched: PersistedSettingKey[];
  /**
   * Which bag of the scoped keys applies: the user's `scheduler_persist_per_clinic`, read on
   * every load (ENG-2194). Written into the document as `scope_mode`, a record only.
   */
  scopeMode: ScopeMode;
}

export interface SchedulerV3State {
  settings: SchedulerSettings;
  /** The cloud settings store's state this session. See `SettingsSyncState`. */
  settingsSync: SettingsSyncState;
  selectedDate: string;
  /** Clinic defaults ran once the catalog loaded; later loads never overwrite a choice. */
  defaultsApplied: boolean;
  /**
   * ENG-2146. A restored staff list skipped the first-visit defaults, but no clinics came
   * back with it (none saved, or every saved one has gone). The route fills the clinic
   * filter alone, through `applyClinicDefaults`, leaving the staff snapshot as saved.
   */
  clinicDefaultsDue: boolean;
  pendingUndo: PendingUndo | null;
  /** Set when an optimistic change was rolled back; the page shows it. */
  statusError: string | null;
  /** A new appointment being started from the grid, a preview or the toolbar. */
  createDraft: CreateDraft | null;
  /**
   * When the current `createDraft` was asked for (`Date.now()`, read in the actions'
   * `prepare`) — the start of the create form's open vital (ENG-2210, `v3-telemetry.ts`).
   */
  createRequestedAt: number | null;
  /** An existing appointment open in the form. Mutually exclusive with `createDraft`. */
  editAppointmentId: number | null;
  /**
   * The SCHEDULE being edited — a personal or clinic event, a work shift or a dedicated
   * slot. A different record on a different endpoint from an appointment, so it cannot
   * share `editAppointmentId`; the two are mutually exclusive and the sheet branches on
   * whichever is set.
   */
  editScheduleId: number | null;
  /**
   * Which kind that schedule is, carried rather than derived. It decides which layout
   * renders, and the sheet must choose that on the first frame — before the schedule
   * detail has arrived. Deriving it from the fetched record means a frame of the wrong
   * form, or a spinner where none is needed.
   */
  editKind: EventKind | null;
  /**
   * Which occurrence of that schedule was clicked (ENG-2180). The form seeds from the series'
   * own row, whose `start_date` is its first day, so the date an edit's "This" or "This and
   * following" acts on has to come from the grid row the pencil or E was pressed on.
   */
  editOccurrence: Occurrence | null;
  /**
   * The appointment being replaced. The form reads the same record as an edit, but seeds
   * a NEW booking from it — see `domain/reschedule.ts` for why that is not a status change.
   */
  rescheduleSourceId: number | null;
  /** A drag-to-move painted on the grid but not yet sent. */
  pendingMove: PendingMove | null;
  /**
   * Moves sent to the server, oldest first, each drawn over the cache until the server's
   * rows have it (`SentMove`). More than one can be out: the Undo timer commits without
   * waiting, and the next drag can commit behind it. Not kept by `leaveScheduler`: nothing
   * on the page is left to draw them.
   */
  sentMoves: SentMove[];
  /** The status change currently asking something in the completion panel. */
  completion: CompletionRequest | null;
  /**
   * Room Traffic moves drawn before the server has answered (ENG-2214), one per
   * appointment. `engine/room-traffic-pending.ts` draws and settles them; the move thunk
   * adds, confirms and drops them.
   */
  roomMoves: PendingRoomMove[];
  /**
   * The event whose preview card is open, by `CalendarEvent.id`. In the store rather than
   * in the grid because the card is no longer drawn by the grid: it docks beside it as a
   * rail, which the PAGE lays out, and the grid only says which event was clicked.
   */
  previewEventId: string | null;
  /**
   * The clicked cell's rectangle, as plain numbers because a `DOMRect` is not
   * serializable. Only used to choose where a card starts once it floats.
   */
  previewAnchor: AnchorRect | null;
  /**
   * The preview that the form sheet displaced, restored when the sheet closes. The sheet
   * is a MODAL drawer, so a card left open behind it is greyed and unclickable — worse
   * than closed, because it looks available. It closes, and comes back.
   */
  previewRestoreId: string | null;
  /**
   * Appointments with a status write in flight. A fact in the store, not a CSS rule:
   * `pointer-events: none` blocks the mouse only, and Enter/Space on a focused cell still
   * fires the handler (ENG-1871).
   */
  statusBusyIds: number[];
  /**
   * Whether the page is wide enough to dock the preview beside the grid (ENG-2009). The
   * page measures it; it is not a setting, because it describes the window, not a choice.
   * `settings.previewMode` stays the user's choice and is never overwritten by this.
   */
  canDock: boolean;
  /**
   * ENG-2104's LaunchDarkly switch (`scheduling-v3-radar`), handed in by `SchedulingV3Routes`
   * because the module reads no flags. UI state, never a setting: it describes the build a
   * person is on, not a choice they made, so it is never saved. Kept by `resetScheduler` and
   * `leaveScheduler` like `canDock`. Off (the default) is V3 as it was before Radar.
   */
  radarEnabled: boolean;
  /**
   * ENG-2666: the classic scheduler's settings as they were on this computer at the moment
   * the person switched to V3, handed over in memory by `useSwitchScheduler().optIn`. The
   * switch happens outside V3 (often before these routes mount), so it waits here until
   * `useClassicCarryOver` has the document and the catalog to act on it, then is cleared.
   * Never saved, and kept by `resetScheduler` and `leaveScheduler`: it is tagged with the
   * user and practice it was taken for, and a scope reset must not lose it.
   */
  classicSnapshot: ClassicSnapshot | null;
  /**
   * Whether the Booking Desk layout and its switch are offered (ENG-2218). The app shell reads
   * the `bookingDeskFormFeature` flag — this module may not import LaunchDarkly — and hands it
   * in through `setBookingDeskOffered`. Not a setting: it describes the rollout, not a choice,
   * so `settings.formLayout` is never overwritten by it. Off until the shell says otherwise.
   */
  bookingDeskOffered: boolean;
  /**
   * SN-19653's `appointmentOpportunityField` flag, handed in by the app shell like
   * `bookingDeskOffered`. On => the form offers "Mark as Opportunity" and the hearing aids
   * on file, and the Day Sheet, the preview and Complete count a hand-marked visit. Off =>
   * all of that reads the appointment TYPE alone, as before. Off until the shell says so.
   */
  opportunityFieldEnabled: boolean;
  /**
   * Manage Work Shifts. Session state on purpose, never a setting: the old mode persisted
   * to localStorage and outlived reloads, logouts and reboots, which produced six "my
   * schedule is gone" tickets (DEV-2731, DEV-2562, DEV-2377, DEV-2783, DEV-2168, DEV-1681).
   * The page turns it off when it unmounts, and a reload starts from `false`.
   */
  workShiftMode: boolean;
  /**
   * "Scheduling for …": a patient sent over from their chart, or an appointment being
   * replaced, with the next slot click meant for them. Session state for the same reason
   * work-shift mode is — see `domain/scheduling-for.ts` (ENG-2032).
   */
  schedulingFor: SchedulingFor | null;
  /**
   * Where the user was before the mode started, once leaving it owes them the trip back.
   * The slice cannot navigate, so ending the mode records the debt here and the page pays
   * it — which keeps every exit (Done, Esc, cancel, save) on one path.
   */
  schedulingForReturn: string | null;
  /**
   * Find Next Available, open with what its caller already knows (checkout seeds the type,
   * the interval and the patient). `null` = closed. Session state, like the form.
   */
  findNext: FindNextRequest | null;
  /**
   * "Book their next appointment?" for the appointment just completed. Shown only while
   * that appointment reads Completed, so an Undo takes the question away with it.
   */
  followUpOffer: FollowUpOffer | null;
  /**
   * The active quick filter, or `null` for none (ENG-2041). Session state, and never a
   * setting: the old slice does not persist it either (`scheduler-slice.ts:344-347`), and
   * a filter that survived a reload would hide most of the day with no visible cause.
   */
  quickFilterCode: QuickFilterCode | null;
  /**
   * The event a Radar row just drilled to (ENG-2104 AC 3): the grid scrolls it into view
   * and pulses it, then clears this. Transient — a highlight is not a preference and must
   * not survive a scope reset or a reload.
   */
  flashEventId: string | null;
  /**
   * Sweep (ENG-2104 AC 10): what a pass HIDES, by day — Keep both, and a Text that sent
   * something. The values are hide keys (`domain/sweep.ts` `sweepHideKeys`): a double
   * booking hides by its members, so it stays hidden when one of them ends or moves;
   * anything else by its item. Slice state, not component state, so leaving Sweep for the
   * grid and coming back does not start the pass over. The rail reads it too, to caption a
   * kept double booking "Sweep skips this".
   */
  sweepSkipped: Record<string, string[]>;
  /**
   * Sweep: the pass's HANDLED rows, by day — one `SweepRecord` (a stripped item and its
   * outcome) per item. Written once the problem has LEFT Radar, never on the press, and
   * forgotten when the item comes back (an Undo, a refusal), so the row is open again
   * (D53, D89). Kept by `leaveScheduler` with `sweepSkipped`.
   */
  sweepHandled: Record<string, SweepRecord[]>;
  /**
   * Sweep: what S / Later put off, by day, as hide keys (D52). Its own list, apart from
   * `sweepSkipped`: a Later item is neither hidden nor counted. Kept by `leaveScheduler`.
   */
  sweepLater: Record<string, string[]>;
  /**
   * Sweep: every booking a rebook in this pass created, from its save on (D58). The pass
   * leaves them out of its list, so a replacement never comes back as Unconfirmed after
   * leaving Sweep and returning in the same session (WB-6). Kept by `leaveScheduler` with
   * `sweepHandled`; `resetScheduler` starts over.
   */
  sweepCreated: number[];
  /** Sweep is on screen (D55): the preview floats and the rail takes no width. */
  sweepActive: boolean;
  /**
   * The view Sweep replaced when it opened from Month (D48): Sweep shows Day, and this is
   * what `sweepClosed` and `leaveScheduler` put back. The swap never calls `markTouched`, and
   * the settings writer reads `viewBeforeSweep ?? settings.view`, so a write that is due,
   * debounced or in flight while Sweep is open saves Month, never Day (R9). `null` when Sweep
   * did not replace the view.
   */
  viewBeforeSweep: SchedulerView | null;
}

export interface FindNextRequest {
  appointmentTypeId?: number | null;
  /** A checkout interval (9 or 18 months included), shown as its own chip when off-preset. */
  startFromMonths?: number | null;
  patient?: { id: number; label: string } | null;
  /** Checkout carries the completed appointment's provider and clinic (`use-schedule-next-appointment.tsx:108-129`). */
  providerIds?: number[];
  clinicId?: number | null;
  /**
   * Who opened the search. It decides what CLOSING does: a search the CHART opened is the
   * chart's errand, so closing it ends "scheduling for" and goes back to the chart
   * (ENG-2027); the others close onto the grid.
   */
  source: "toolbar" | "checkout" | "chart";
  /**
   * A pick MOVES this appointment rather than booking a new one (Radar's "More times"). The
   * search sizes its slots by `durationMinutes`, the appointment's own length, not its
   * type's; `notify` is the channel the resolver chose, passed on to the move.
   */
  move?: {
    appointmentId: number;
    durationMinutes: number;
    notify?: NotifyKeys;
  } | null;
  /**
   * A pick fills Sweep's rebook tray (D46, "More times"): the search sizes its slots by the
   * booking's own length, and `bookFoundSlot` sets the reschedule mode's pick without
   * opening the form or moving the grid's date.
   */
  rebook?: { durationMinutes: number } | null;
}

const initialSettingsSync = (): SettingsSyncState => ({
  status: "idle",
  hydratedFor: null,
  boundPracticeId: null,
  lateDocument: null,
  settingsNotice: "none",
  holdReason: null,
  touched: [],
  scopeMode: "all"
});

export const initialSchedulerV3State = (today: string): SchedulerV3State => ({
  settings: DEFAULT_SETTINGS,
  settingsSync: initialSettingsSync(),
  selectedDate: today,
  defaultsApplied: false,
  clinicDefaultsDue: false,
  pendingUndo: null,
  statusError: null,
  createDraft: null,
  createRequestedAt: null,
  editAppointmentId: null,
  editScheduleId: null,
  editKind: null,
  editOccurrence: null,
  rescheduleSourceId: null,
  pendingMove: null,
  sentMoves: [],
  completion: null,
  roomMoves: [],
  previewEventId: null,
  previewAnchor: null,
  previewRestoreId: null,
  statusBusyIds: [],
  canDock: true,
  radarEnabled: false,
  classicSnapshot: null,
  bookingDeskOffered: false,
  opportunityFieldEnabled: false,
  workShiftMode: false,
  schedulingFor: null,
  schedulingForReturn: null,
  findNext: null,
  followUpOffer: null,
  quickFilterCode: null,
  flashEventId: null,
  sweepSkipped: {},
  sweepHandled: {},
  sweepLater: {},
  sweepCreated: [],
  sweepActive: false,
  viewBeforeSweep: null
});

// Reducers stay pure: "today" is read in the action creator, never in the reducer.
const today = () => ({ payload: isoDate(new Date()) });

/**
 * This change is the user's, so the store owes the server a write of exactly these keys.
 *
 * Anything without a validator is ignored, which is how `slotMinutes` — written by the
 * route on every mount from `Practice.scheduling_cell_duration` — never becomes a write.
 */
const markTouched = (
  state: SchedulerV3State,
  keys: readonly string[]
): void => {
  const { touched } = state.settingsSync;
  for (const key of keys) {
    if (isPersistedSettingKey(key) && !touched.includes(key)) {
      touched.push(key);
    }
  }
};

/** The three keys any staff-list edit writes together. */
const STAFF_KEYS = ["staffListIds", "providerIds", "staffPreset"] as const;

/** A document has been applied, so the 3 s cap has nothing left to release. */
const APPLIED: readonly SettingsSyncStatus[] = ["hydrated", "cacheHydrated"];

/**
 * Starting "scheduling for", from either entry.
 *
 * Starting it twice behaves like starting it once (ENG-2032, AC 4): the subject is
 * REPLACED rather than stacked, and whatever was open above the grid comes down, so a
 * second start can never leave a card describing the first subject on screen.
 *
 * It also stands work-shift mode down. The two modes mean different things by the same
 * click, and the old app's precedence (rescheduling → patient-create → work shift) is
 * expressed here, by entering, rather than resolved at every click site.
 */
const beginSchedulingFor = (
  state: SchedulerV3State,
  mode: SchedulingFor
): void => {
  state.schedulingFor = mode;
  state.workShiftMode = false;
  state.previewEventId = null;
  state.previewAnchor = null;
  state.previewRestoreId = null;
  state.createDraft = null;
  state.editAppointmentId = null;
  state.editScheduleId = null;
  state.editKind = null;
  state.editOccurrence = null;
  state.rescheduleSourceId = null;
};

/** Append ids to a day's list, once each. */
const addOnce = (
  map: Record<string, string[]>,
  date: string,
  ids: readonly string[]
): void => {
  const list = map[date] ?? [];
  const fresh = ids.filter(
    (id, i) => !list.includes(id) && ids.indexOf(id) === i
  );
  if (fresh.length) map[date] = [...list, ...fresh];
};

/** Put a record under its item's day, replacing any earlier one for the same item (D53). */
const upsertRecord = (
  map: Record<string, SweepRecord[]>,
  record: SweepRecord
): void => {
  const { id, date } = record.item;
  const others = (map[date] ?? []).filter((r) => r.item.id !== id);
  map[date] = [...others, record];
};

/**
 * Take "scheduling for" down and every form it opened — the body of `exitSchedulingFor`
 * without its trip back to a chart, so Sweep closing can end a reschedule it started
 * (defect 1) without owing a navigation.
 */
const endSchedulingFor = (state: SchedulerV3State): void => {
  state.schedulingFor = null;
  state.createDraft = null;
  state.editAppointmentId = null;
  state.editScheduleId = null;
  state.editKind = null;
  state.editOccurrence = null;
  state.rescheduleSourceId = null;
};

/** What RTK Query puts on every lifecycle action of an events read. */
interface EventsReadAction {
  meta: { requestId: string; arg: { originalArgs: EventsArgs } };
}

/**
 * RTK Query's lifecycle actions for a read of `getV3Events`, matched by the endpoint name
 * RTK Query puts on each of them. Not `getV3Events.matchPending`: this slice is in the
 * app's main bundle (`app/store.ts`), and importing the V3 API would pull it in too.
 */
const readsEvents =
  (phase: (action: unknown) => boolean) =>
  (action: UnknownAction): action is UnknownAction & EventsReadAction => {
    if (!phase(action)) return false;
    const { arg } = action.meta as {
      arg?: { type?: unknown; endpointName?: unknown };
    };
    return arg?.type === "query" && arg.endpointName === "getV3Events";
  };

/** A read of the range the grid draws, keyed as `selectEventsArgs` keys it. */
const isGridRange = (state: SchedulerV3State, args: EventsArgs): boolean => {
  const grid = eventsArgsFor(
    state.settings.view,
    state.settings.clinicIds,
    state.selectedDate
  );
  return (
    grid.from === args.from &&
    grid.to === args.to &&
    grid.clinicIds.join() === args.clinicIds.join()
  );
};

/** A found slot as the reschedule mode's pick. */
const pickFromDraft = ({
  date,
  start,
  end,
  providerId,
  clinicId
}: CreateDraft): ReschedulePick => ({
  date,
  startMinutes: start,
  endMinutes: end,
  providerId: providerId ?? null,
  clinicId: clinicId ?? null
});

/** Open the form on the replacement for the appointment the mode is rescheduling. */
const openRescheduleForm = (state: SchedulerV3State): void => {
  const mode = state.schedulingFor;
  if (mode?.kind !== "reschedule") return;
  state.editAppointmentId = mode.appointmentId;
  state.rescheduleSourceId = mode.appointmentId;
  state.createDraft = null;
  state.editScheduleId = null;
  state.editKind = null;
  state.editOccurrence = null;
};

/** An action that opens the form, stamped with when it was asked for (ENG-2210). */
type RequestedAction<P> = PayloadAction<P, string, { requestedAt: number }>;

/** `prepare` for the create actions: the clock is read here, never in the reducer. */
const withRequestedAt = <P>(payload: P) => ({
  payload,
  meta: { requestedAt: Date.now() }
});

const slice = createSlice({
  name: "schedulerV3",
  initialState: initialSchedulerV3State(isoDate(new Date())),
  reducers: {
    setSelectedDate(state, action: PayloadAction<string>) {
      state.selectedDate = action.payload;
    },
    completionRequested(state, action: PayloadAction<CompletionRequest>) {
      state.completion = action.payload;
    },
    completionDismissed(state) {
      state.completion = null;
    },
    /**
     * A Room Traffic drop or menu pick: draw the card in its destination now. Replaces
     * a CONFIRMED move of the same card; the thunk never starts one over an in-flight one.
     */
    roomMoveStarted(
      state,
      action: PayloadAction<{
        moveId: number;
        appointmentId: number;
        to: number;
      }>
    ) {
      const { moveId, appointmentId, to } = action.payload;
      state.roomMoves = state.roomMoves.filter(
        (m) => m.appointmentId !== appointmentId
      );
      state.roomMoves.push({
        moveId,
        appointmentId,
        to,
        row: null,
        confirmedAt: null
      });
    },
    /**
     * The write and its re-read came back clean; keep drawing it until the data agrees.
     * Matched by move, so a late answer for a move that is gone changes nothing.
     */
    roomMoveConfirmed(
      state,
      action: PayloadAction<
        Pick<PendingRoomMove, "moveId" | "row"> & { confirmedAt: number }
      >
    ) {
      const move = state.roomMoves.find(
        (m) => m.moveId === action.payload.moveId
      );
      if (!move) return;
      move.row = action.payload.row;
      move.confirmedAt = action.payload.confirmedAt;
    },
    /** Stop drawing these moves, by move id: the data agrees, the write failed, or two rooms. */
    roomMovesDropped(state, action: PayloadAction<number[]>) {
      const ids = new Set(action.payload);
      state.roomMoves = state.roomMoves.filter((m) => !ids.has(m.moveId));
    },
    statusWriteStarted(state, action: PayloadAction<number>) {
      // An id, not a counter: two starts for the same appointment describe one write.
      if (!state.statusBusyIds.includes(action.payload))
        state.statusBusyIds.push(action.payload);
    },
    statusWriteFinished(state, action: PayloadAction<number>) {
      state.statusBusyIds = state.statusBusyIds.filter(
        (id) => id !== action.payload
      );
    },
    goToday: {
      reducer(state, action: PayloadAction<string>) {
        state.selectedDate = action.payload;
      },
      // The caller may pass the grid's own "today", the clinic's day (ENG-2034,
      // `ui/grid/use-grid-now.hook.ts`). With no argument it is the machine's, as before.
      prepare: (date?: string) => (date ? { payload: date } : today())
    },
    step(state, action: PayloadAction<1 | -1>) {
      state.selectedDate = stepDate(
        state.settings.view,
        state.selectedDate,
        action.payload
      );
    },
    updateSettings(state, action: PayloadAction<Partial<SchedulerSettings>>) {
      Object.assign(state.settings, action.payload);
      markTouched(state, Object.keys(action.payload));
      // A view the user picks is theirs: Sweep's Month override no longer has one to put
      // back, and the writer must send this one, not Month (D48).
      if (action.payload.view !== undefined) state.viewBeforeSweep = null;
    },
    toggleProvider(state, action: PayloadAction<number>) {
      const ids = state.settings.providerIds;
      const at = ids.indexOf(action.payload);
      if (at >= 0) ids.splice(at, 1);
      else ids.push(action.payload);
      markTouched(state, ["providerIds"]);
    },
    /** Replace the working list with a quick set; everyone on it is shown. */
    setStaffList(state, action: PayloadAction<StaffList>) {
      state.settings.staffListIds = action.payload.ids;
      state.settings.providerIds = [...action.payload.ids];
      state.settings.staffPreset = action.payload.preset;
      markTouched(state, STAFF_KEYS);
    },
    addToStaffList(state, action: PayloadAction<ProviderId>) {
      const { settings } = state;
      if (!settings.staffListIds.includes(action.payload))
        settings.staffListIds.push(action.payload);
      if (!settings.providerIds.includes(action.payload))
        settings.providerIds.push(action.payload);
      settings.staffPreset = null;
      markTouched(state, STAFF_KEYS);
    },
    removeFromStaffList(state, action: PayloadAction<ProviderId>) {
      const { settings } = state;
      settings.staffListIds = settings.staffListIds.filter(
        (id) => id !== action.payload
      );
      settings.providerIds = settings.providerIds.filter(
        (id) => id !== action.payload
      );
      settings.staffPreset = null;
      markTouched(state, STAFF_KEYS);
    },
    /** ENG-2666: the switch read classic's settings; V3 acts on them once it can. */
    classicSnapshotTaken(state, action: PayloadAction<ClassicSnapshot>) {
      state.classicSnapshot = action.payload;
    },
    classicSnapshotConsumed(state) {
      state.classicSnapshot = null;
    },
    /**
     * ENG-2666: the carry-over has been saved, so it is laid out — as the person's own change,
     * which is what it is once they have seen it. `markTouched` lets the store's writer send
     * these keys again (the same values, so nothing changes on the server) and, when it lands,
     * hand the saved document to the read half, so a later clinic switch re-reads the bags
     * that were just written rather than the empty document the page opened with.
     *
     * A key the person already changed this session is theirs and is left alone, as
     * `hydrateSettings` does.
     */
    classicCarryOverApplied(
      state,
      action: PayloadAction<Partial<SchedulerSettings>>
    ) {
      const held = new Set<string>(state.settingsSync.touched);
      const keys = Object.keys(action.payload).filter((key) => !held.has(key));
      for (const key of keys as (keyof SchedulerSettings)[]) {
        state.settings[key] = action.payload[key] as never;
      }
      markTouched(state, keys);
      if (keys.includes("view")) state.viewBeforeSweep = null;
      // A copied staff list is a choice: the clinic's defaults must not run over it.
      if (keys.includes("staffListIds") && state.settings.staffListIds.length)
        state.defaultsApplied = true;
      if (keys.includes("clinicIds") && state.settings.clinicIds.length)
        state.clinicDefaultsDue = false;
    },
    /** "Reorder Scheduling Columns" saved (ENG-2166). The staff list itself is untouched. */
    setColumnOrder(state, action: PayloadAction<ProviderId[]>) {
      state.settings.columnOrder = action.payload;
      markTouched(state, ["columnOrder"]);
    },
    /**
     * First load, once: every clinic when none is chosen, and the staff list. The user's
     * "select all staff" profile setting (`scheduler_select_all_staff`) opens on "Everyone",
     * as classic's staff filter does on a first visit (`use-staff-filter.hook.tsx`,
     * ENG-2141 U1); otherwise the clinic team (the clinic's default staff, else the
     * signed-in user). A stored "Just me" is honoured; when the user is not on the staff list
     * it draws everyone but the stored preset stays "me" (ENG-2209) — the control shows
     * Everyone through `shownPreset`, and the preference is not overwritten.
     */
    applyStaffDefaults(
      state,
      action: PayloadAction<
        StaffContext & {
          selectAllStaff?: boolean;
          /** The route's stale "Just me" re-default (ENG-2209), the one rerun allowed. */
          reapply?: boolean;
        }
      >
    ) {
      // ENG-2298: the route decides to dispatch from its last render, which can predate a
      // hydration landing in the same commit (a slow load whose settings read timed out).
      // Judge from the store as it is now, or the defaults overwrite the restored list.
      if (state.defaultsApplied && !action.payload.reapply) return;
      const clinicIds = state.settings.clinicIds.length
        ? state.settings.clinicIds
        : action.payload.clinicIds;
      const storedMe = state.settings.staffPreset === "me";
      const firstPreset = storedMe
        ? "me"
        : action.payload.selectAllStaff
          ? "everyone"
          : "team";
      const { ids, preset } = staffForPreset(firstPreset, {
        ...action.payload,
        clinicIds
      });
      state.settings.clinicIds = clinicIds;
      state.settings.staffListIds = ids;
      state.settings.providerIds = [...ids];
      state.settings.staffPreset = storedMe ? "me" : preset;
      state.defaultsApplied = true;
      state.clinicDefaultsDue = false;
    },

    /**
     * ENG-2146: the clinic half of the first-visit defaults, for a reload whose stored staff
     * list came back without clinics. Only fills an empty filter, and is not a user change,
     * so it is never persisted.
     */
    applyClinicDefaults(
      state,
      action: PayloadAction<{ clinicIds: ClinicId[] }>
    ) {
      // Same stale-render guard as above (ENG-2298): a filter saved empty on purpose
      // hydrates with nothing due, and must stay empty.
      if (!state.clinicDefaultsDue) return;
      if (state.settings.clinicIds.length === 0) {
        state.settings.clinicIds = action.payload.clinicIds;
      }
      state.clinicDefaultsDue = false;
    },

    /**
     * ENG-2047. The read for this scope has started; every write is bound to it.
     *
     * `scopeMode` is the user's own mode, from `User.scheduler_persist_per_clinic` (ENG-2194:
     * it decides on every load; the document never overrides it). It is in place from the
     * first frame so that a change made after the 3 s cap — before any document exists — is
     * written into the bag that mode calls for.
     */
    settingsReadStarted(
      state,
      action: PayloadAction<{
        hydratedFor: string;
        boundPracticeId: number;
        scopeMode: ScopeMode;
      }>
    ) {
      state.settingsSync.status = "reading";
      state.settingsSync.hydratedFor = action.payload.hydratedFor;
      state.settingsSync.boundPracticeId = action.payload.boundPracticeId;
      state.settingsSync.scopeMode = action.payload.scopeMode;
    },
    /**
     * A stored document against today's data (ENG-2047 §6.2).
     *
     * Two rules carry the whole design. A key the user has already changed this session is
     * left alone, which is what makes Apply safe — it can never undo something the user
     * did after the page opened. And the staff list is the stored SNAPSHOT filtered to
     * people who still load, never re-derived from `staffPreset` (D4/D14): a list saved as
     * "Clinic team" does not pick up a new hire until the user re-picks the preset, which
     * is exactly what the old scheduler does.
     *
     * `source: "scope"` is the third caller (PR 4): the header clinic moved, so the five
     * scoped keys are re-read out of the document already in hand. It reconciles exactly as
     * a read does but reports nothing about the server — the gate, the notice and any hold
     * are all left where they were.
     */
    hydrateSettings(
      state,
      action: PayloadAction<{
        /** Validated keys the document carried; absent keys keep what is on screen. */
        values: Partial<SchedulerSettings>;
        /** Clinics that exist for this user today. */
        clinicIds: ClinicId[];
        /** Providers that actually loaded. */
        providerIds: ProviderId[];
        appointmentTypeIds: AppointmentTypeId[];
        scopeMode: ScopeMode;
        source: "cache" | "server" | "scope";
      }>
    ) {
      const { values, scopeMode, source } = action.payload;
      const sync = state.settingsSync;
      const { settings } = state;
      const held = new Set<string>(sync.touched);
      const carried = (key: PersistedSettingKey): boolean =>
        key in values && !held.has(key);

      for (const key of Object.keys(values) as PersistedSettingKey[]) {
        // The five scoped keys are reconciled below; everything else is taken as read.
        if (
          key === "clinicIds" ||
          key === "providerIds" ||
          key === "staffListIds" ||
          key === "appointmentTypeIds" ||
          !carried(key)
        ) {
          continue;
        }
        settings[key] = values[key] as never;
      }

      if (carried("clinicIds")) {
        // An empty result is not a wipe: `applyStaffDefaults` fills it with every clinic.
        settings.clinicIds = (values.clinicIds ?? []).filter((id) =>
          action.payload.clinicIds.includes(id)
        );
      }
      if (carried("appointmentTypeIds")) {
        const stored = values.appointmentTypeIds;
        settings.appointmentTypeIds =
          stored === null
            ? null
            : (stored ?? []).filter((id) =>
                action.payload.appointmentTypeIds.includes(id)
              );
      }
      if (carried("staffListIds")) {
        const alive = (values.staffListIds ?? []).filter((id) =>
          action.payload.providerIds.includes(id)
        );
        settings.staffListIds = alive;
        // Whoever is left on the list stays shown; the clinic defaults are not re-run
        // unless everybody on the saved list has gone.
        settings.providerIds = settings.providerIds.filter((id) =>
          alive.includes(id)
        );
        if (alive.length > 0) state.defaultsApplied = true;
      }
      if (carried("providerIds")) {
        settings.providerIds = (values.providerIds ?? []).filter((id) =>
          settings.staffListIds.includes(id)
        );
      }

      // ENG-2146. An empty clinic filter is the person's choice only when they saved it empty
      // (or emptied it this session); otherwise nothing chose it, and it must not stay empty.
      const savedClinics = carried("clinicIds")
        ? (values.clinicIds ?? [])
        : null;
      state.clinicDefaultsDue =
        settings.clinicIds.length === 0 &&
        !held.has("clinicIds") &&
        (savedClinics === null || savedClinics.length > 0);

      sync.scopeMode = scopeMode;
      if (source === "scope") {
        // A clinic whose bag holds no staff list has never been opened, so there is nothing
        // to restore and the clinic's own team is the right answer — which is what the old
        // scheduler does on a first visit (`use-staff-filter.hook.tsx:520-523`). Letting the
        // route's `applyStaffDefaults` run again is how that happens.
        // A saved list whose people have all gone is the same: nothing left to restore.
        if (!carried("staffListIds") || settings.staffListIds.length === 0)
          state.defaultsApplied = false;
        // The preset those defaults honour must be THIS clinic's (ENG-2209). `staffPreset` is
        // a scoped key, so a bag without one has never chosen a quick set; keeping the last
        // clinic's "Just me" on screen would make `applyStaffDefaults` draw Just me here
        // instead of the clinic team. A held (touched) value is left alone, as above.
        if (!("staffPreset" in values) && !held.has("staffPreset"))
          settings.staffPreset = null;
        // A clinic with no saved order starts in list order, as classic does for a clinic
        // it has no `schedulingProvidersOrder` entry for (ENG-2166).
        if (!carried("columnOrder")) settings.columnOrder = [];
        return;
      }
      sync.status = source === "cache" ? "cacheHydrated" : "hydrated";
      if (source === "server") {
        // A read that lands lifts a hold from an earlier failure; a version hold is set
        // again by the hook, which is the only thing that can see the stored `v`.
        if (sync.holdReason !== "writeFailed") sync.holdReason = null;
        sync.lateDocument = null;
        sync.settingsNotice = "none";
      }
    },
    /** The 3 s cap. A no-op once anything has been applied — see §6.1. */
    settingsReadTimedOut(state) {
      if (APPLIED.includes(state.settingsSync.status)) return;
      state.settingsSync.status = "timedOut";
    },
    settingsReadFailed(
      state,
      action: PayloadAction<"unavailable" | "readFailed">
    ) {
      if (!APPLIED.includes(state.settingsSync.status)) {
        state.settingsSync.status = "readFailed";
      }
      state.settingsSync.holdReason = action.payload;
    },
    settingsHoldChanged(
      state,
      action: PayloadAction<SettingsHoldReason | null>
    ) {
      state.settingsSync.holdReason = action.payload;
    },
    /** Keys the server has stored, and whose value has not changed since it was sent. */
    settingsAcknowledged(state, action: PayloadAction<PersistedSettingKey[]>) {
      const done = new Set<string>(action.payload);
      state.settingsSync.touched = state.settingsSync.touched.filter(
        (key) => !done.has(key)
      );
    },
    /** A write that can never land — a practice mismatch, a 400, a switched tenant. */
    settingsWriteDropped(state) {
      state.settingsSync.touched = [];
    },
    /**
     * The header clinic moved and the writer has already snapshotted these keys to send
     * under the clinic they were made in (PR 4). They are no longer this scope's to save, so
     * they stop being touched — otherwise the next debounce would write the clinic the user
     * has just left's values into the clinic they have just opened, and the swap below would
     * skip them as "the user's own change".
     *
     * Identical in effect to `settingsWriteDropped` and deliberately separate: that one says
     * a write is LOST, this one says it is in flight somewhere else.
     */
    settingsScopeHandedOff(state) {
      state.settingsSync.touched = [];
    },
    /** A document that arrived too late to lay the screen out without being asked (D2). */
    settingsLateDocumentOffered(state, action: PayloadAction<unknown>) {
      state.settingsSync.lateDocument = action.payload;
      state.settingsSync.settingsNotice = "offered";
    },
    /** Apply: the hook owns it, because the reconciliation needs today's catalog. */
    settingsNoticeApplyRequested(state) {
      state.settingsSync.settingsNotice = "applying";
    },
    /** Keep current. For this session only; nothing is persisted about a dismissal. */
    settingsNoticeDismissed(state) {
      state.settingsSync.settingsNotice = "dismissed";
      state.settingsSync.lateDocument = null;
    },
    /** Nothing left to offer — the user hand-changed everything it was going to apply. */
    settingsNoticeWithdrawn(state) {
      state.settingsSync.settingsNotice = "none";
      state.settingsSync.lateDocument = null;
    },
    showUndo(state, action: PayloadAction<PendingUndo>) {
      state.pendingUndo = action.payload;
      state.statusError = null;
    },
    dismissUndo(state) {
      state.pendingUndo = null;
    },
    showMoveUndo(state, action: PayloadAction<PendingMove>) {
      state.pendingMove = action.payload;
      state.statusError = null;
    },
    dismissMove(state) {
      state.pendingMove = null;
    },
    /**
     * The held move's window closed: it leaves `pendingMove` and joins `sentMoves` in ONE
     * action, so no render can fall between the two and draw the old slot.
     */
    moveSaving(state, action: PayloadAction<PendingMove>) {
      state.pendingMove = null;
      state.sentMoves.push({ move: action.payload, saved: false });
    },
    /**
     * Its PATCH settled. A saved move waits for a newer read of the grid's range; a refused
     * one stops being drawn at once. Matched on the move itself, never on the appointment
     * alone: two moves of one card can be out at the same time.
     */
    moveSaved(
      state,
      action: PayloadAction<{ move: PendingMove; saved: boolean }>
    ) {
      const { move, saved } = action.payload;
      const i = state.sentMoves.findIndex((s) => isSameMove(s.move, move));
      if (i < 0) return;
      if (saved) state.sentMoves[i].saved = true;
      else state.sentMoves.splice(i, 1);
    },
    // A failed save is never the pending change (it is dismissed before it is sent), so a
    // newer Undo offer for another appointment stays.
    statusFailed(state, action: PayloadAction<string>) {
      state.statusError = action.payload;
    },
    clearStatusError(state) {
      state.statusError = null;
    },
    /** A cell was clicked: show its card floating beside the anchor, or on the rail when docked. */
    openPreview(
      state,
      action: PayloadAction<{ id: string; anchor: AnchorRect | null }>
    ) {
      state.previewEventId = action.payload.id;
      state.previewAnchor = action.payload.anchor;
      state.previewRestoreId = null;
    },
    closePreview(state) {
      state.previewEventId = null;
      state.previewAnchor = null;
      state.previewRestoreId = null;
    },
    /**
     * Dragged off the rail. The mode is **sticky**: it is a setting, so the next card —
     * and the next session, on any computer — opens the same way.
     */
    floatPreview(state) {
      state.settings.previewMode = "floating";
      markTouched(state, ["previewMode"]);
    },
    dockPreview(state) {
      state.settings.previewMode = "docked";
      markTouched(state, ["previewMode"]);
    },
    setCanDock(state, action: PayloadAction<boolean>) {
      state.canDock = action.payload;
    },
    /** The route boundary's flag, never the user's change: no `markTouched`, so never saved. */
    setRadarEnabled(state, action: PayloadAction<boolean>) {
      state.radarEnabled = action.payload;
    },
    setBookingDeskOffered(state, action: PayloadAction<boolean>) {
      state.bookingDeskOffered = action.payload;
    },
    setOpportunityFieldEnabled(state, action: PayloadAction<boolean>) {
      state.opportunityFieldEnabled = action.payload;
    },
    toggleSidePanel(state) {
      state.settings.sidePanelCollapsed = !state.settings.sidePanelCollapsed;
      markTouched(state, ["sidePanelCollapsed"]);
    },
    /** Radar (ENG-2104). A setting, like the side panel's fold — see `SchedulerSettings`. */
    toggleRadar(state) {
      state.settings.radarOpen = !state.settings.radarOpen;
      markTouched(state, ["radarOpen"]);
    },
    setRadarOpen(state, action: PayloadAction<boolean>) {
      state.settings.radarOpen = action.payload;
      markTouched(state, ["radarOpen"]);
    },
    /**
     * A Radar row was tapped: step to its day and mark the cell for the grid to scroll to
     * and pulse. Opening the card is NOT part of this — a card folds Radar to its strip,
     * and a tap meant "show me" would then hide the list it came from. The cell is one
     * click away as always (Chen's Q5, defaulted 2026-09-23; ledger D3).
     */
    drillTo(state, action: PayloadAction<{ eventId: string; date: string }>) {
      state.selectedDate = action.payload.date;
      state.flashEventId = action.payload.eventId;
    },
    clearFlash(state) {
      state.flashEventId = null;
    },
    /**
     * Sweep: Keep both, or a Text that sent something. A decision, so the hide IS the
     * departure: it hides by `keys` and records the row with its outcome (D53, D62).
     */
    hideInSweep(
      state,
      action: PayloadAction<{
        date: string;
        keys: string[];
        record: SweepRecord;
      }>
    ) {
      const { date, keys, record } = action.payload;
      addOnce(state.sweepSkipped, date, keys);
      upsertRecord(state.sweepHandled, record);
    },
    /**
     * Sweep: the item's problem has left Radar. Ticks its row; a second record for the same
     * item replaces the first (D53). Never hides — see `sweepHandled`.
     */
    recordSweepHandled(state, action: PayloadAction<SweepRecord>) {
      upsertRecord(state.sweepHandled, action.payload);
    },
    /** Sweep: the item is back (an Undo, a refusal), so its row is open again (R4, D89). */
    forgetSweepRecord(
      state,
      action: PayloadAction<{ date: string; id: string }>
    ) {
      const { date, id } = action.payload;
      const list = state.sweepHandled[date];
      if (!list) return;
      const kept = list.filter((record) => record.item.id !== id);
      if (kept.length) state.sweepHandled[date] = kept;
      else delete state.sweepHandled[date];
    },
    /** Sweep: a rebook saved a new booking, which this pass leaves out of its list (D58). */
    sweepRebookSaved(state, action: PayloadAction<number>) {
      if (!state.sweepCreated.includes(action.payload))
        state.sweepCreated.push(action.payload);
    },
    /** Sweep: S / Later puts the item off, uncounted and still actionable (D52). */
    laterInSweep(
      state,
      action: PayloadAction<{ date: string; keys: string[] }>
    ) {
      addOnce(state.sweepLater, action.payload.date, action.payload.keys);
    },
    /**
     * Sweep is on screen: the card floats and the rail takes no width (D55).
     *
     * Opened from Month (D48), Sweep shows Day on `monthDay`, the first item's day (D76).
     * That is Sweep's view, not the user's: nothing is marked touched, so it is never saved
     * (`drill` would have saved it), and `sweepClosed` puts Month back.
     */
    sweepOpened(
      state,
      action: PayloadAction<{ monthDay?: string } | undefined>
    ) {
      state.sweepActive = true;
      const monthDay = action.payload?.monthDay;
      if (monthDay && state.settings.view === "month") {
        state.viewBeforeSweep = "month";
        state.settings.view = "day";
        state.selectedDate = monthDay;
      }
    },
    /**
     * Sweep has gone. A reschedule started in it must not leak onto the main page as the
     * panel and frame (defect 1, ENG-2208); a patient mode was started elsewhere and is left
     * alone. The rebook
     * tray's Find Next goes with it: without the mode, its pick would book a second
     * appointment. Month comes back if Sweep replaced it, without `markTouched` (D48).
     */
    sweepClosed(state) {
      state.sweepActive = false;
      if (state.schedulingFor?.kind === "reschedule") endSchedulingFor(state);
      if (state.findNext?.rebook) state.findNext = null;
      if (state.viewBeforeSweep) state.settings.view = state.viewBeforeSweep;
      state.viewBeforeSweep = null;
    },
    /**
     * The form sheet is opening over the card. Remember it and take it down; `closeForm`
     * puts it back. Called by the page rather than by every action that opens the form,
     * so a new entry point cannot forget.
     */
    yieldPreviewToForm(state) {
      if (!state.previewEventId) return;
      state.previewRestoreId = state.previewEventId;
      state.previewEventId = null;
    },
    restorePreviewAfterForm(state) {
      if (!state.previewRestoreId) return;
      state.previewEventId = state.previewRestoreId;
      state.previewRestoreId = null;
    },
    /**
     * Two reducers, not a toggle, for the reason the old slice gives
     * (`disableWorkShiftViewMode`): the mode has more than one way out, and two toggles
     * firing in one interaction cancel out. Both close the card, whose cell changes
     * meaning under it (a booking becomes a ghost, a shift becomes shading).
     */
    /**
     * Click a counter to narrow the grid; click the same one again to clear it (AC 2).
     *
     * The toggle lives here rather than in the bar so every way of setting a filter
     * behaves the same — and so a second entry point added later cannot forget the
     * "click it again" half of the rule.
     */
    toggleQuickFilter(state, action: PayloadAction<QuickFilterCode>) {
      state.quickFilterCode =
        state.quickFilterCode === action.payload ? null : action.payload;
    },
    clearQuickFilter(state) {
      state.quickFilterCode = null;
    },
    enterWorkShiftMode(state) {
      state.workShiftMode = true;
      // The bar is gone in this mode (AC 6), so a filter left active would go on hiding
      // appointments with nothing on screen to say why, and no control to undo it.
      state.quickFilterCode = null;
      // One mode at a time: an empty slot cannot mean both "add a shift" and "book this
      // patient here". Managing shifts is the one the user just asked for, so it wins.
      state.schedulingFor = null;
      state.previewEventId = null;
      state.previewAnchor = null;
      state.previewRestoreId = null;
    },
    exitWorkShiftMode(state) {
      if (!state.workShiftMode) return;
      state.workShiftMode = false;
      state.previewEventId = null;
      state.previewAnchor = null;
      state.previewRestoreId = null;
    },
    openCreateDraft: {
      reducer(state, action: RequestedAction<CreateDraft>) {
        // `slotIntent` attaches the mode's patient to a clicked time, but two surfaces start
        // a draft without it: a dedicated block's "New Appointment" and the preview's "create
        // here". The old mode attaches the patient there too, so it is guarded HERE, where the
        // next caller cannot forget it (ENG-2027). An appointment only; a draft that names
        // its own patient keeps it, and a block keeps its own type.
        const draft = action.payload;
        const mode = state.schedulingFor;
        const isAppointment = !draft.kind || draft.kind === "appointment";
        state.createDraft =
          mode?.kind === "patient" && isAppointment && !draft.patient
            ? {
                ...draft,
                patient: mode.patient,
                ...(draft.appointmentTypeId ? {} : suggestedType(mode))
              }
            : draft;
        state.editAppointmentId = null;
        state.editScheduleId = null;
        state.editKind = null;
        state.editOccurrence = null;
        state.rescheduleSourceId = null;
        state.createRequestedAt = action.meta.requestedAt;
      },
      prepare: (draft: CreateDraft) => withRequestedAt(draft)
    },
    /** The edit pencil, and every surface that lands on an existing appointment. */
    openEditAppointment(state, action: PayloadAction<number>) {
      state.editAppointmentId = action.payload;
      state.editScheduleId = null;
      state.editKind = null;
      state.editOccurrence = null;
      state.createDraft = null;
      state.rescheduleSourceId = null;
    },
    /**
     * The same pencil, on a schedule row. The preview gates it with `canEdit`
     * (`domain/deletable.ts`), which is deliberately looser than delete — a chart-noted
     * appointment can be edited but not deleted, and a block, personal event or work
     * shift is always editable.
     */
    openEditSchedule(
      state,
      action: PayloadAction<{
        scheduleId: number;
        kind: EventKind;
        occurrence: Occurrence | null;
      }>
    ) {
      state.editScheduleId = action.payload.scheduleId;
      state.editKind = action.payload.kind;
      state.editOccurrence = action.payload.occurrence;
      state.editAppointmentId = null;
      state.createDraft = null;
      state.rescheduleSourceId = null;
    },
    /**
     * Reschedule starts the mode rather than the form (ENG-2032, AC 3). Before this it
     * opened the form at once, and the only way to pick a time was the form's own Find a
     * time — a grid click during a reschedule started an unrelated new draft. The panel's
     * "Open form" keeps the old one-click path.
     *
     * The form still reads the source through `editAppointmentId`, with
     * `rescheduleSourceId` saying what to do with the result — one read path, two seeds.
     */
    openReschedule(state, action: PayloadAction<RescheduleRequest>) {
      beginSchedulingFor(state, {
        kind: "reschedule",
        ...action.payload,
        pick: null,
        returnToPath: null
      });
    },
    /** Start the mode for a patient sent over from their chart. */
    enterSchedulingFor(state, action: PayloadAction<SchedulingFor>) {
      beginSchedulingFor(state, action.payload);
    },
    /**
     * The one way out, whoever asked — Done, Esc, the form's Cancel on a save, or the
     * page unmounting. `returnToStart` is what a deliberate exit passes: it records the
     * chart to go back to, which the page then navigates to. An exit that IS a
     * navigation (leaving the page) passes nothing, so nothing bounces.
     */
    exitSchedulingFor(
      state,
      action: PayloadAction<{ returnToStart?: boolean } | undefined>
    ) {
      const mode = state.schedulingFor;
      if (!mode) return;
      if (action.payload?.returnToStart && mode.returnToPath)
        state.schedulingForReturn = mode.returnToPath;
      endSchedulingFor(state);
    },
    /** The page has navigated back; the debt is paid. */
    clearSchedulingForReturn(state) {
      state.schedulingForReturn = null;
    },
    /** "Open form" on the panel, with no slot picked: the replacement at its old time. */
    openRescheduleAtSource(state) {
      openRescheduleForm(state);
    },
    /**
     * A slot picked for the replacement. One gesture, so one action: the new time is
     * recorded on the mode and the form opens on it. The form applies it over the seed
     * read from the source (`use-appointment-form.hook.ts`).
     */
    pickRescheduleSlot(state, action: PayloadAction<ReschedulePick>) {
      const mode = state.schedulingFor;
      if (mode?.kind !== "reschedule") return;
      mode.pick = action.payload;
      // In Sweep the mode is the rebook tray (D47, L1): the pick is the tray's new time, and
      // the form opens only from its "Open full form" (`openRescheduleAtSource`).
      if (state.sweepActive) return;
      openRescheduleForm(state);
    },
    /** Esc's first step in the rebook tray: the picked time goes, the mode stays (D46). */
    clearReschedulePick(state) {
      const mode = state.schedulingFor;
      if (mode?.kind === "reschedule") mode.pick = null;
    },
    /** One close for both modes — the sheet does not care which opened it. */
    closeForm(state) {
      state.createDraft = null;
      state.editAppointmentId = null;
      state.editScheduleId = null;
      state.editKind = null;
      state.editOccurrence = null;
      state.rescheduleSourceId = null;
    },
    /**
     * "+ New" and N: a new appointment with no person or column yet.
     *
     * Seeded at the current clock time for an hour, which is what the old form does with
     * no appointment to read (`use-appointments.hook.tsx:1068-1076`: `new Date()` and
     * `addMinutes(new Date(), 60)`). One deliberate difference: the day is the one on
     * screen rather than always today — pressing New while looking at next Tuesday and
     * landing on today is the old surface's bug, not its behaviour.
     */
    openCreateForDay: {
      reducer(
        state,
        action: RequestedAction<{ startMinutes: number; kind?: EventKind }>
      ) {
        // "+ New ▾" names a kind (ENG-2286); N and the button leave it to the mode.
        const kind =
          action.payload.kind ??
          (state.workShiftMode ? ("workShift" as const) : undefined);
        state.createDraft = {
          date: state.selectedDate,
          start: action.payload.startMinutes,
          end: action.payload.startMinutes + 60,
          providerId: null,
          clinicId: null,
          source: "toolbar",
          // "New" starts a shift while the mode is on (Chen, 2026-09-18).
          ...(kind ? { kind } : {}),
          // …and it is for the mode's patient while THAT mode is on, which is what
          // "the form is also reachable without clicking a slot" means (ENG-2032, AC 2).
          // Only an appointment has a patient.
          ...((kind ?? "appointment") === "appointment" &&
          state.schedulingFor?.kind === "patient"
            ? {
                patient: state.schedulingFor.patient,
                // …with the type the chart suggested, if any (ENG-2027).
                ...suggestedType(state.schedulingFor)
              }
            : {})
        };
        state.editAppointmentId = null;
        state.createRequestedAt = action.meta.requestedAt;
      },
      // "Now" is read here, never in the reducer.
      prepare: (kind?: EventKind) => {
        const now = new Date();
        return {
          payload: {
            startMinutes: now.getHours() * 60 + now.getMinutes(),
            ...(kind ? { kind } : {})
          },
          meta: { requestedAt: now.getTime() }
        };
      }
    },
    closeCreateDraft(state) {
      state.createDraft = null;
    },
    openFindNext(state, action: PayloadAction<FindNextRequest>) {
      state.findNext = action.payload;
    },
    closeFindNext(state) {
      state.findNext = null;
    },
    offerFollowUp(state, action: PayloadAction<FollowUpOffer | null>) {
      state.followUpOffer = action.payload;
    },
    /**
     * A picked opening becomes a draft in ONE step: the sheet closes and the form opens in
     * the same render, at the same edge, so the hand-off never shows an empty frame.
     * Nothing is created until the form is saved (`find-next-available-dialog:317-350`).
     */
    bookFoundSlot: {
      reducer(state, action: RequestedAction<CreateDraft>) {
        state.findNext = null;
        const mode = state.schedulingFor;
        // Sweep's rebook tray (D46, defect 2): the pick fills the tray's time and nothing
        // else. No form, and no `selectedDate`, which Sweep's queue used to read (D48).
        if (state.sweepActive && mode?.kind === "reschedule") {
          mode.pick = pickFromDraft(action.payload);
          return;
        }
        // D14: the grid goes to the day being booked, so the form opens over that day and the
        // saved card lands in view. Measured without it: "Appointment created" over a grid
        // still showing today, and the booking nowhere on screen.
        state.selectedDate = action.payload.date;
        // During a reschedule the found slot is the replacement's time, exactly as a grid
        // click is (`pickRescheduleSlot`): the form opens on the source's seed, and saving
        // marks the source Rescheduled. Booked as a plain draft it left the patient with two
        // appointments — measured in the ENG-2104 write proof (Radar "Rebook"). Behind the
        // Radar flag: off, the pick books a plain draft exactly as before ENG-2104.
        if (state.radarEnabled && mode?.kind === "reschedule") {
          mode.pick = pickFromDraft(action.payload);
          openRescheduleForm(state);
          return;
        }
        state.createDraft = action.payload;
        state.editAppointmentId = null;
        state.editScheduleId = null;
        state.editKind = null;
        state.editOccurrence = null;
        state.rescheduleSourceId = null;
        state.createRequestedAt = action.meta.requestedAt;
      },
      prepare: (draft: CreateDraft) => withRequestedAt(draft)
    },
    resetScheduler: {
      // A clean slate for the new user or region — except `canDock`, which describes the
      // window, not the user. The page measures it once per resize, so wiping it here
      // docked the card into a 71 px grid until the window next changed size (ENG-2009).
      // `sweepActive` likewise describes the screen, and only Sweep's mount and unmount move
      // it (`sweepOpened` / `sweepClosed`). A scope switch does unmount Sweep: the reset
      // wipes `settingsSync`, so the route renders <Waiting/> until the new read settles
      // (scheduling-v3.routes.tsx), and that unmount clears it. Keeping it here is harmless.
      // `radarEnabled` is the build, not the user: only the route boundary sets it, on mount
      // and when the flag changes, so a reset (which runs while the routes stay mounted) that
      // wiped it would turn Radar off for the rest of the visit.
      reducer: (state, action: PayloadAction<string>) => ({
        ...initialSchedulerV3State(action.payload),
        canDock: state.canDock,
        radarEnabled: state.radarEnabled,
        classicSnapshot: state.classicSnapshot,
        sweepActive: state.sweepActive,
        // The flag, like the window, outlives a new user or region: the shell sets it only
        // when it changes, so wiping it would hide the desk until the next flag change.
        bookingDeskOffered: state.bookingDeskOffered,
        opportunityFieldEnabled: state.opportunityFieldEnabled
      }),
      prepare: today
    },
    // Leaving the page keeps where the user was — the date and every setting (view,
    // filters, staff, the folded panel) — and drops everything transient, so no card, form,
    // draft or pending move comes back with it (ENG-2019, D4). A new user or region is
    // still `resetScheduler`.
    //
    // `sweepSkipped`, `sweepHandled`, `sweepLater` and `sweepCreated` are kept too (ENG-2104
    // hardening, D52, WB-6):
    // they are what a Sweep pass IS, and must outlive a trip elsewhere in the app the same
    // way the date and the view do, not just a trip to Room Traffic that never unmounts these
    // routes at all. `sweepActive` is not: nothing is on screen after leaving, so a Month
    // that Sweep replaced with Day comes back here too (D48).
    leaveScheduler: (state) => ({
      ...initialSchedulerV3State(state.selectedDate),
      settings: state.viewBeforeSweep
        ? { ...state.settings, view: state.viewBeforeSweep }
        : state.settings,
      // The read is kept with the settings it produced: a trip to a chart and back is the
      // same session, so it must not re-hydrate, and an unsaved change must not be lost.
      settingsSync: state.settingsSync,
      defaultsApplied: state.defaultsApplied,
      clinicDefaultsDue: state.clinicDefaultsDue,
      canDock: state.canDock,
      radarEnabled: state.radarEnabled,
      classicSnapshot: state.classicSnapshot,
      sweepSkipped: state.sweepSkipped,
      sweepHandled: state.sweepHandled,
      sweepLater: state.sweepLater,
      sweepCreated: state.sweepCreated,
      bookingDeskOffered: state.bookingDeskOffered,
      opportunityFieldEnabled: state.opportunityFieldEnabled
    })
  },
  extraReducers: (builder) => {
    builder
      // A read of the grid's range that starts after a save can only carry the saved
      // position, so each saved move waits for the first such read to land. One that
      // started earlier never counts, however late it lands. The save's own refetch can
      // start a moment before `moveSaved`; the move is then drawn, where the server has
      // it, until the next read lands.
      .addMatcher(readsEvents(isPending), (state, { meta }) => {
        const waiting = state.sentMoves.filter((s) => s.saved && !s.readId);
        if (!waiting.length || !isGridRange(state, meta.arg.originalArgs))
          return;
        for (const sent of waiting) sent.readId = meta.requestId;
      })
      .addMatcher(readsEvents(isFulfilled), (state, { meta }) => {
        if (state.sentMoves.some((s) => s.readId === meta.requestId))
          state.sentMoves = state.sentMoves.filter(
            (s) => s.readId !== meta.requestId
          );
      })
      // A failed read brought no rows: its moves wait for the next one.
      .addMatcher(readsEvents(isRejected), (state, { meta }) => {
        for (const sent of state.sentMoves)
          if (sent.readId === meta.requestId) delete sent.readId;
      });
  }
});

export const {
  classicSnapshotTaken,
  classicSnapshotConsumed,
  classicCarryOverApplied,
  applyClinicDefaults,
  setSelectedDate,
  goToday,
  step,
  updateSettings,
  toggleProvider,
  setStaffList,
  addToStaffList,
  removeFromStaffList,
  setColumnOrder,
  applyStaffDefaults,
  showUndo,
  dismissUndo,
  showMoveUndo,
  dismissMove,
  moveSaving,
  moveSaved,
  statusFailed,
  clearStatusError,
  openPreview,
  closePreview,
  floatPreview,
  dockPreview,
  setCanDock,
  setRadarEnabled,
  setBookingDeskOffered,
  setOpportunityFieldEnabled,
  toggleSidePanel,
  toggleRadar,
  setRadarOpen,
  drillTo,
  clearFlash,
  hideInSweep,
  recordSweepHandled,
  forgetSweepRecord,
  laterInSweep,
  sweepRebookSaved,
  sweepOpened,
  sweepClosed,
  enterWorkShiftMode,
  exitWorkShiftMode,
  toggleQuickFilter,
  clearQuickFilter,
  enterSchedulingFor,
  exitSchedulingFor,
  clearSchedulingForReturn,
  openRescheduleAtSource,
  pickRescheduleSlot,
  clearReschedulePick,
  yieldPreviewToForm,
  restorePreviewAfterForm,
  openCreateDraft,
  openFindNext,
  closeFindNext,
  offerFollowUp,
  bookFoundSlot,
  openCreateForDay,
  closeCreateDraft,
  openEditAppointment,
  openEditSchedule,
  openReschedule,
  closeForm,
  completionRequested,
  completionDismissed,
  roomMoveStarted,
  roomMoveConfirmed,
  roomMovesDropped,
  statusWriteStarted,
  statusWriteFinished,
  settingsReadStarted,
  hydrateSettings,
  settingsReadTimedOut,
  settingsReadFailed,
  settingsHoldChanged,
  settingsAcknowledged,
  settingsWriteDropped,
  settingsScopeHandedOff,
  settingsLateDocumentOffered,
  settingsNoticeApplyRequested,
  settingsNoticeDismissed,
  settingsNoticeWithdrawn,
  resetScheduler,
  leaveScheduler
} = slice.actions;

export default slice.reducer;

// Selectors live beside the slice; re-exported so importers keep one path.
export * from "./scheduler-v3.selectors";
